/* eslint-disable @typescript-eslint/default-param-last */
import { FileObject } from 'src/interfaces/shared';
import API_BASE_URL from 'src/constants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import { Product } from './products';
import { Partner } from './partner';
import { StockItem } from './inventory';
import { ChatItem } from './chat';

import type { components, paths } from '../generated/v1';
import { $api, $apiFetchClient } from './api';

export const fetchAllSaleOrderIds = async (domain?: string): Promise<number[]> => {
  const accessToken = localStorage.getItem('access_token');
  if (!accessToken) {
    throw new Error('Unauthorized');
  }

  let url = `${API_BASE_URL}portal/sale_orders/ids`;
  if (domain) {
    url += `?domain=${encodeURIComponent(domain)}`;
  }

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch sale order ids');
  }

  return response.json();
};

export interface NewSaleOrderItem {
  name: string;
  partner: Partner;
  is_palette: boolean;
  attachments: FileObject[];
  order_note: string;
  order_lines: {
    product_id: number;
    quantity: number;
  }[];
  active?: boolean;
}

export interface SaleOrderItem {
  amount_total: number;
  attachments: FileObject[];
  chat: ChatItem[];
  carrier: Carrier;
  carrier_id: number;
  commitment_date: string;
  company_id: number;
  connector: string;
  create_date: string;
  df_delivery_status: string;
  fulfillment_cost: number;
  id: number;
  is_palette: boolean;
  is_self_collection: boolean;
  name: string;
  order_lines: OrderLine[];
  order_note: string;
  package_count: number;
  partner: Partner;
  warehouse_country: string;
  warehouse_id: number;
  incoterm_id?: number | null;
  is_cod?: boolean;
  cod_amount?: number | null;
  cod_currency_id?: number | null;
}

export interface Carrier {
  id: number;
  logo_url: string;
  name: string;
  shipping_lines: ShippingLine[];
}

export interface ShippingLine {
  picking_id: string;
  tracking_id: number;
  tracking_state: string;
  tracking_url: string;
}

export interface OrderLine {
  id: number;
  product: Product | StockItem | null;
  product_status: string | null;
  quantity: number;
}

// TODO(api-types): drop the two intersections below once the
// address-confirmation backend fields are deployed to staging and
// `yarn generate:api-code` has picked them up in the generated schemas.
// wms_hold (spec 2026-10-07 §9.5) follows the same rule: regenerate after
// the OMS slice (clientportal `development`) is on staging, then drop it.
export type SaleOrder = components['schemas']['SaleOrder'] & {
  address_confirmed?: boolean;
  show_address_confirm?: boolean;
  wms_hold?: boolean;
};
export type SaleOrderCreate = components['schemas']['SaleOrderCreate'];
export type SaleOrderLine = components['schemas']['SaleOrderLine'];
export type SaleOrderLineLot = NonNullable<SaleOrderLine['lot']>;
export type SaleOrderUpdate = components['schemas']['SaleOrderUpdate'] & {
  address_confirmed?: boolean | null;
};

// TODO(api-types): drop once incoterm/COD/FBA are back in the generated schema
// (backend revert 1a1a64ff — see flow-sync-oms task-2 ledger). Fields are
// consumed across Tasks 2 (incoterm), 3 (COD) and 4 (FBA label).
export type SaleOrderFlowsyncFields = {
  incoterm_id?: number | null;
  is_cod?: boolean;
  cod_amount?: number | null;
  cod_currency_id?: number | null;
};
export type SaleOrderFbaLabel = components['schemas']['FbaLabel'] | null;

export type SaleOrderCreateExt = SaleOrderCreate & SaleOrderFlowsyncFields;
export type SaleOrderUpdateExt = SaleOrderUpdate & SaleOrderFlowsyncFields;
export type SaleOrderReadExt = SaleOrder & SaleOrderFlowsyncFields & { fba_label?: SaleOrderFbaLabel };

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['get', '/sale_orders'] });
  queryClient.invalidateQueries({ queryKey: ['get', '/sale_orders/{sale_order_id}'] });
}

export const useSaleOrdersQuery = (params: paths['/sale_orders']['get']['parameters']['query']) => {
  return $api.useQuery('get', '/sale_orders', {
    params: {
      query: params,
    },
  });
};

export const useSaleOrderQuery = (id: number) => {
  return $api.useQuery('get', `/sale_orders/{sale_order_id}`, {
    params: {
      path: {
        sale_order_id: id,
      },
    },
  });
};

export const useSaleOrderUpdate = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('put', '/sale_orders/{sale_order_id}', {
    onSuccess() {
      invalidate(queryClient);
    },
  });
};

export const useSaleOrderCreate = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('post', '/sale_orders', {
    onSuccess() {
      invalidate(queryClient);
    },
  });
};

export const useSaleOrderLineDelete = () => {
  const queryClient = useQueryClient();
  const deleteMutation = $api.useMutation('delete', '/sale_orders/sale_order_line/{sale_order_line_id}', {
    onSuccess() {
      invalidate(queryClient);
    },
  });
  return deleteMutation;
};

export const useCancelSaleOrder = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('put', '/utility/bulk_action/{model}/cancel', {
    onSuccess() {
      invalidate(queryClient);
    },
  });
};

export const useConfirmSaleOrder = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('put', '/utility/bulk_action/{model}/confirm', {
    onSuccess() {
      invalidate(queryClient);
    },
  });
};

export const useSaleOrderCancel = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('post', '/sale_orders/{sale_order_id}/cancel', {
    onSuccess() {
      toast.success(t('outbound.cancel_success'));
      invalidate(queryClient);
    },
  });
};

export const useSaleOrderConfirm = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('post', '/sale_orders/{sale_order_id}/confirm', {
    onSuccess() {
      toast.success(t('outbound.confirm_success'));
      invalidate(queryClient);
    },
  });
};

export const useSaleOrderSetToDraft = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('post', '/sale_orders/{sale_order_id}/draft', {
    onSuccess() {
      toast.success(t('outbound.set_to_draft_success'));
      invalidate(queryClient);
    },
  });
};

export const useSaleOrderMutations = () => {
  const queryClient = useQueryClient();

  const duplicateSaleOrders = async (ids: number[]) => {
    const results = await Promise.all(
      ids.map(id =>
        $apiFetchClient.POST('/sale_orders/{sale_order_id}/duplicate', {
          params: { path: { sale_order_id: id } },
        }),
      ),
    );
    invalidate(queryClient);
    return results;
  };

  return {
    duplicateSaleOrders,
  };
};

// Endpoint (POST /sale_orders/{sale_order_id}/fba_label, multipart `file`) is not
// yet in the generated v1.d.ts — same transitional pattern as the untyped GETs in
// incoterm.ts/codSettings.ts, but for a mutation: openapi-fetch only accepts an
// unknown path/body shape via this cast. Multipart body follows utility.ts's
// uploadImportTemplate (FormData cast to the declared `{ file: string }` shape —
// openapi-fetch sets the multipart Content-Type/boundary itself when it sees a
// real FormData instance at runtime).
export const useFbaLabelUpload = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ saleOrderId, file }: { saleOrderId: number; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await ($apiFetchClient as { POST: Function }).POST('/sale_orders/{sale_order_id}/fba_label', {
        params: { path: { sale_order_id: saleOrderId } },
        body: formData as unknown as { file: string },
      });
      if (response.error) {
        // Carry the HTTP status alongside the parsed error body so callers can
        // tell 413 (too large) / 415 (wrong type) / 403 FORBIDDEN_STATE (order
        // cancelled) apart without re-parsing the raw Response themselves.
        const error = new Error('FBA label upload failed') as Error & { status?: number; detail?: unknown };
        error.status = response.response?.status;
        error.detail = (response.error as { detail?: unknown } | undefined)?.detail ?? response.error;
        throw error;
      }
      return response.data as NonNullable<SaleOrderFbaLabel>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['get', '/sale_orders/{sale_order_id}'] });
    },
  });
  return {
    upload: (saleOrderId: number, file: File) => mutation.mutateAsync({ saleOrderId, file }),
    isPending: mutation.isPending,
  };
};

export const useSetDraftSaleOrder = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('put', '/utility/bulk_action/{model}/set_draft', {
    onSuccess() {
      invalidate(queryClient);
    },
  });
};

// Function to delete a sale order by ID
// export const deleteSaleOrderById = async (id: number) => {
//   const accessToken = localStorage.getItem('access_token');
//   try {
//     if (!id) return;

//     const response = await fetch(`${API_BASE_URL}portal/sale_orders/${id}`, {
//       headers: {
//         Authorization: `Bearer ${accessToken}`,
//         'Content-Type': 'application/json',
//       },
//       method: 'DELETE',
//     });

//     if (!response.ok) {
//       if (response.status === 401) {
//         localStorage.removeItem('access_token');
//       }
//       throw new Error('Failed to delete sale order');
//     }

//     if (response.status === 204) {
//       toast.success(i18n.t('success.delete_sale_order_success'));
//     }
//   } catch (err) {
//     console.error(err);
//     toast.error(i18n.t('errors.delete_sale_order_failed'));
//   }
// };
